import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  X,
  GraduationCap,
  BookOpen,
  Users as UsersIcon,
  Mail,
  Phone,
  Calendar,
  Award,
  Briefcase,
  User as UserIcon
} from 'lucide-react';
import './TeachersPage.css';

const AVATAR_PALETTE = [
  ['#fef3d8', '#8a540a'],
  ['#e8f1e2', '#3b6b2e'],
  ['#fde6e6', '#a91e2c'],
  ['#e9e2ef', '#4b2a6b'],
  ['#e2ecf3', '#1f567d'],
  ['#f7e9d8', '#7a4618'],
];
const initialsOf = (t) =>
  `${(t.firstName || '').charAt(0)}${(t.lastName || '').charAt(0)}`.toUpperCase() || '?';
const colorFor = (key = '') => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

const TeachersPage = () => {
  // State for teachers data
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for modals
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // State for filters
  const [filters, setFilters] = useState({
    search: '',
    subject: '',
    page: 1,
    pageSize: 10
  });

  // State for form
  const [formData, setFormData] = useState({
    employeeId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    dateOfBirth: '',
    qualification: '',
    specialization: '',
    subjects: '',
    joiningDate: ''
  });

  // State for pagination
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1
  });

  // State for available subjects (for filter dropdown)
  const [availableSubjects, setAvailableSubjects] = useState([]);

  // Fetch teachers on component mount and filter change
  useEffect(() => {
    fetchTeachers();
  }, [filters]);

  // Fetch available subjects
  useEffect(() => {
    fetchAvailableSubjects();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams({
        page: filters.page,
        pageSize: filters.pageSize,
        ...(filters.search && { search: filters.search }),
        ...(filters.subject && { subject: filters.subject })
      });

      const response = await api.get(`/teachers?${queryParams}`);

      const data = response.data.teachers;
      setTeachers(Array.isArray(data) ? data : []);
      setPagination(response.data.pagination || {
        total: 0,
        page: filters.page,
        pageSize: filters.pageSize,
        totalPages: 0
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch teachers');
      console.error('Error fetching teachers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSubjects = async () => {
    try {
      const response = await api.get('/subjects?limit=100');
      const subjectsData = response.data.subjects || response.data || [];
      // Extract unique subject names (API returns full Subject objects)
      const uniqueNames = [...new Set(subjectsData.map(s => typeof s === 'string' ? s : s.name))];
      setAvailableSubjects(uniqueNames);
    } catch (err) {
      console.error('Error fetching subjects:', err);
    }
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setSelectedTeacher(null);
    setFormData({
      employeeId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      gender: '',
      dateOfBirth: '',
      qualification: '',
      specialization: '',
      subjects: '',
      joiningDate: ''
    });
    setShowModal(true);
  };

  const handleEditClick = (teacher) => {
    setIsEditing(true);
    setSelectedTeacher(teacher);
    setFormData({
      employeeId: teacher.employeeId,
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      email: teacher.email,
      phone: teacher.phone,
      gender: teacher.gender || '',
      dateOfBirth: teacher.dateOfBirth || '',
      qualification: teacher.qualification || '',
      specialization: teacher.specialization || '',
      subjects: Array.isArray(teacher.subjects)
        ? teacher.subjects.join(', ')
        : teacher.subjects || '',
      joiningDate: teacher.joiningDate || ''
    });
    setShowModal(true);
  };

  const handleViewClick = (teacher) => {
    setSelectedTeacher(teacher);
    setShowViewModal(true);
  };

  const handleDeleteClick = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacher? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/teachers/${teacherId}`);
      setTeachers(teachers.filter(t => t.id !== teacherId));
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to delete teacher');
      console.error('Error deleting teacher:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.employeeId || !formData.firstName || !formData.lastName || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Convert comma-separated subjects string to array
      const subjectsArray = formData.subjects
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const payload = {
        ...formData,
        subjects: subjectsArray
      };

      if (isEditing && selectedTeacher) {
        await api.put(`/teachers/${selectedTeacher.id}`, payload);
      } else {
        await api.post('/teachers', payload);
      }

      setShowModal(false);
      setFormData({
        employeeId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gender: '',
        dateOfBirth: '',
        qualification: '',
        specialization: '',
        subjects: '',
        joiningDate: ''
      });

      await fetchTeachers();
    } catch (err) {
      setError(err.message || 'Failed to save teacher');
      console.error('Error saving teacher:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page on filter change
    }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setFilters(prev => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const getSubjectBadges = (subjects) => {
    if (!subjects) return null;
    const subjectsArray = Array.isArray(subjects) ? subjects : [];
    return subjectsArray.map((subject, index) => (
      <span key={index} className="badge badge-info">
        {typeof subject === 'object' ? subject.name : subject}
      </span>
    ));
  };

  const subjectCount = new Set(
    teachers.flatMap(t => Array.isArray(t.subjects) ? t.subjects : [])
  ).size;
  const classesCovered = new Set(
    teachers.flatMap(t => Array.isArray(t.classesAssigned) ? t.classesAssigned : [])
  ).size;

  return (
    <div className="tv-page">
      {/* Top bar */}
      <div className="tv-topbar">
        <div className="tv-titleblock">
          <span className="tv-eyebrow">People</span>
          <h1 className="tv-title">Teachers</h1>
          <p className="tv-sub">Directory of all teaching staff and their assigned classes.</p>
        </div>

        <div className="tv-stats">
          <div className="tv-stat">
            <UsersIcon size={16} className="tv-stat-ic" />
            <div>
              <div className="tv-stat-num">{pagination.total || teachers.length}</div>
              <div className="tv-stat-lbl">Teachers</div>
            </div>
          </div>
          <div className="tv-stat">
            <BookOpen size={16} className="tv-stat-ic" />
            <div>
              <div className="tv-stat-num">{subjectCount}</div>
              <div className="tv-stat-lbl">Subjects</div>
            </div>
          </div>
          <div className="tv-stat">
            <GraduationCap size={16} className="tv-stat-ic" />
            <div>
              <div className="tv-stat-num">{classesCovered}</div>
              <div className="tv-stat-lbl">Classes covered</div>
            </div>
          </div>
        </div>

        <button className="tv-btn-primary" onClick={handleAddClick}>
          <Plus size={16} />
          Add teacher
        </button>
      </div>

      {/* Toolbar */}
      <div className="tv-toolbar">
        <div className="tv-search">
          <Search size={16} className="tv-search-ic" />
          <input
            type="text"
            placeholder="Search by name, email, or employee ID"
            value={filters.search}
            onChange={handleSearchChange}
          />
        </div>

        <select
          name="subject"
          className="tv-select"
          value={filters.subject}
          onChange={handleFilterChange}
        >
          <option value="">All subjects</option>
          {availableSubjects.map(subject => (
            <option key={subject} value={subject}>{subject}</option>
          ))}
        </select>
      </div>

      {error && <div className="tv-error">{error}</div>}

      {/* Table card */}
      <div className="tv-card">
        {loading ? (
          <div className="tv-loading">
            <div className="tv-spinner" />
            <span>Loading teachers…</span>
          </div>
        ) : teachers.length === 0 ? (
          <div className="tv-empty">
            <GraduationCap size={40} />
            <h3>No teachers yet</h3>
            <p>Add your first teacher to start assigning classes and subjects.</p>
            <button className="tv-btn-primary" onClick={handleAddClick}>
              <Plus size={16} /> Add teacher
            </button>
          </div>
        ) : (
          <div className="tv-table-wrap">
            <table className="tv-table">
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>Teacher</th>
                  <th style={{ width: '22%' }}>Contact</th>
                  <th>Subjects</th>
                  <th style={{ width: '14%' }}>Classes</th>
                  <th style={{ width: '130px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map(teacher => {
                  const [bg, fg] = colorFor(teacher.employeeId || teacher.email || '');
                  const subs = Array.isArray(teacher.subjects) ? teacher.subjects : [];
                  const classes = Array.isArray(teacher.classesAssigned) ? teacher.classesAssigned : [];
                  return (
                    <tr key={teacher.id}>
                      <td>
                        <div className="tv-person">
                          <span className="tv-avatar" style={{ background: bg, color: fg }}>
                            {initialsOf(teacher)}
                          </span>
                          <div className="tv-person-text">
                            <div className="tv-person-name">
                              {teacher.firstName} {teacher.lastName}
                            </div>
                            <div className="tv-person-meta">{teacher.employeeId}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="tv-contact">
                          <div className="tv-email">{teacher.email}</div>
                          <div className="tv-phone">{teacher.phone || '—'}</div>
                        </div>
                      </td>
                      <td>
                        {subs.length ? (
                          <div className="tv-chips">
                            {subs.slice(0, 3).map((s, i) => (
                              <span key={i} className="tv-chip tv-chip-subject">
                                {typeof s === 'object' ? s.name : s}
                              </span>
                            ))}
                            {subs.length > 3 && (
                              <span className="tv-chip tv-chip-more">+{subs.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="tv-muted">—</span>
                        )}
                      </td>
                      <td>
                        {classes.length ? (
                          <div className="tv-chips">
                            {classes.slice(0, 4).map((c, i) => (
                              <span key={i} className="tv-chip tv-chip-class">{c}</span>
                            ))}
                            {classes.length > 4 && (
                              <span className="tv-chip tv-chip-more">+{classes.length - 4}</span>
                            )}
                          </div>
                        ) : (
                          <span className="tv-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="tv-actions">
                          <button
                            className="tv-icon-btn"
                            onClick={() => handleViewClick(teacher)}
                            title="View"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            className="tv-icon-btn"
                            onClick={() => handleEditClick(teacher)}
                            title="Edit"
                          >
                            <Edit size={15} />
                          </button>
                          <button
                            className="tv-icon-btn tv-icon-danger"
                            onClick={() => handleDeleteClick(teacher.id)}
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="tv-pagination">
            <span className="tv-pagination-info">
              Showing page {pagination.page} of {pagination.totalPages}
              {pagination.total > 0 && ` · ${pagination.total} total`}
            </span>
            <div className="tv-pagination-actions">
              <button
                className="tv-btn-ghost"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <button
                className="tv-btn-ghost"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Teacher Modal */}
      {showModal && (
        <div className="tv-overlay" onClick={() => !loading && setShowModal(false)}>
          <div className="tv-modal tv-modal-form" onClick={e => e.stopPropagation()}>
            <button
              className="tv-modal-close"
              onClick={() => !loading && setShowModal(false)}
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="tv-modal-head">
              <span className="tv-eyebrow">{isEditing ? 'Edit profile' : 'Onboard teacher'}</span>
              <h2 className="tv-modal-title">
                {isEditing ? 'Edit teacher details' : 'Add a new teacher'}
              </h2>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="tv-modal-body">
                {error && <div className="tv-error" style={{ marginBottom: 16 }}>{error}</div>}

                <section className="tv-section">
                  <h4 className="tv-section-title">Identity</h4>
                  <div className="tv-form-grid">
                    <label className="tv-field">
                      <span>Employee ID *</span>
                      <input
                        type="text"
                        name="employeeId"
                        value={formData.employeeId}
                        onChange={handleFormChange}
                        disabled={isEditing}
                        required
                      />
                    </label>
                    <label className="tv-field">
                      <span>First name *</span>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleFormChange}
                        required
                      />
                    </label>
                    <label className="tv-field">
                      <span>Last name *</span>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleFormChange}
                        required
                      />
                    </label>
                    <label className="tv-field">
                      <span>Gender</span>
                      <select name="gender" value={formData.gender} onChange={handleFormChange}>
                        <option value="">Select…</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label className="tv-field">
                      <span>Date of birth</span>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleFormChange}
                      />
                    </label>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Contact</h4>
                  <div className="tv-form-grid">
                    <label className="tv-field tv-field-wide">
                      <span>Email *</span>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                      />
                    </label>
                    <label className="tv-field">
                      <span>Phone</span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleFormChange}
                      />
                    </label>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Education & teaching</h4>
                  <div className="tv-form-grid">
                    <label className="tv-field">
                      <span>Qualification</span>
                      <input
                        type="text"
                        name="qualification"
                        placeholder="e.g. M.Ed, B.Sc"
                        value={formData.qualification}
                        onChange={handleFormChange}
                      />
                    </label>
                    <label className="tv-field">
                      <span>Specialization</span>
                      <input
                        type="text"
                        name="specialization"
                        placeholder="e.g. Physics, Literature"
                        value={formData.specialization}
                        onChange={handleFormChange}
                      />
                    </label>
                    <label className="tv-field">
                      <span>Joining date</span>
                      <input
                        type="date"
                        name="joiningDate"
                        value={formData.joiningDate}
                        onChange={handleFormChange}
                      />
                    </label>
                    <label className="tv-field tv-field-full">
                      <span>Subjects</span>
                      <input
                        type="text"
                        name="subjects"
                        placeholder="Comma-separated, e.g. Mathematics, Science"
                        value={formData.subjects}
                        onChange={handleFormChange}
                      />
                      <small className="tv-field-hint">Separate multiple subjects with commas.</small>
                    </label>
                  </div>
                </section>
              </div>

              <div className="tv-modal-footer">
                <button
                  type="button"
                  className="tv-btn-ghost"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tv-btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving…' : (isEditing ? 'Save changes' : 'Add teacher')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Teacher Modal */}
      {showViewModal && selectedTeacher && (() => {
        const [bg, fg] = colorFor(selectedTeacher.employeeId || selectedTeacher.email || '');
        const subs = Array.isArray(selectedTeacher.subjects) ? selectedTeacher.subjects : [];
        const classes = Array.isArray(selectedTeacher.classesAssigned) ? selectedTeacher.classesAssigned : [];
        return (
          <div className="tv-overlay" onClick={() => setShowViewModal(false)}>
            <div className="tv-modal tv-modal-view" onClick={e => e.stopPropagation()}>
              <button
                className="tv-modal-close"
                onClick={() => setShowViewModal(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>

              {/* Hero */}
              <div className="tv-modal-hero">
                <span className="tv-modal-avatar" style={{ background: bg, color: fg }}>
                  {initialsOf(selectedTeacher)}
                </span>
                <div className="tv-modal-hero-text">
                  <div className="tv-modal-name">
                    {selectedTeacher.firstName} {selectedTeacher.lastName}
                  </div>
                  <div className="tv-modal-meta">
                    <span className="tv-modal-pill">{selectedTeacher.employeeId}</span>
                    {selectedTeacher.qualification && (
                      <span className="tv-modal-pill-quiet">{selectedTeacher.qualification}</span>
                    )}
                    {selectedTeacher.gender && (
                      <span className="tv-modal-pill-quiet">
                        {selectedTeacher.gender.charAt(0).toUpperCase() + selectedTeacher.gender.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="tv-modal-body">
                <section className="tv-section">
                  <h4 className="tv-section-title">Contact</h4>
                  <div className="tv-infogrid">
                    <div className="tv-info">
                      <Mail size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Email</div>
                        <div className="tv-info-val">{selectedTeacher.email}</div>
                      </div>
                    </div>
                    <div className="tv-info">
                      <Phone size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Phone</div>
                        <div className="tv-info-val">{selectedTeacher.phone || '—'}</div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Profile</h4>
                  <div className="tv-infogrid">
                    <div className="tv-info">
                      <Calendar size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Date of birth</div>
                        <div className="tv-info-val">{selectedTeacher.dateOfBirth || '—'}</div>
                      </div>
                    </div>
                    <div className="tv-info">
                      <Briefcase size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Joining date</div>
                        <div className="tv-info-val">{selectedTeacher.joiningDate || '—'}</div>
                      </div>
                    </div>
                    <div className="tv-info">
                      <Award size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Specialization</div>
                        <div className="tv-info-val">{selectedTeacher.specialization || '—'}</div>
                      </div>
                    </div>
                    <div className="tv-info">
                      <UserIcon size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Gender</div>
                        <div className="tv-info-val">
                          {selectedTeacher.gender
                            ? selectedTeacher.gender.charAt(0).toUpperCase() + selectedTeacher.gender.slice(1)
                            : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Teaching</h4>
                  <div className="tv-teach">
                    <div>
                      <div className="tv-teach-lbl">Subjects</div>
                      {subs.length ? (
                        <div className="tv-chips">
                          {subs.map((s, i) => (
                            <span key={i} className="tv-chip tv-chip-subject">
                              {typeof s === 'object' ? s.name : s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="tv-muted">No subjects assigned</span>
                      )}
                    </div>
                    <div>
                      <div className="tv-teach-lbl">Classes assigned</div>
                      {classes.length ? (
                        <div className="tv-chips">
                          {classes.map((c, i) => (
                            <span key={i} className="tv-chip tv-chip-class">Class {c}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="tv-muted">No classes yet</span>
                      )}
                    </div>
                  </div>
                </section>
              </div>

              {/* Footer */}
              <div className="tv-modal-footer">
                <button
                  className="tv-btn-ghost"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
                <button
                  className="tv-btn-primary"
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditClick(selectedTeacher);
                  }}
                >
                  <Edit size={15} />
                  Edit teacher
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TeachersPage;
